/**
 * WHAT A TEACHER HAS WRITTEN ONTO A DRAWING: names, colours, electrons, numbers.
 *
 * ── ONE FIELD, NOT FOUR ───────────────────────────────────────────────────────────────────────
 * A structure token is `[smiles:…|name|arrows]`, and every one of those fields has to be read
 * and written in FIVE places — `structureFields` and `serializeToken` here in the token layer,
 * both directions in `reaction-doc`, and the Tiptap node's attributes. A field added to four of
 * the five is lost silently, which is a shape this codebase has been bitten by before.
 *
 * So the four annotations share one field and the five registries are paid once:
 * `[smiles:…|name|arrows|n1=hydroxide;c4=#cc3333;e4=2020;l1=1]`.
 *
 * ── WHAT THE KEYS MEAN ────────────────────────────────────────────────────────────────────────
 *   n<part>   the name of one species inside the drawing, numbered as `anchorFacts` numbers
 *             fragments — from 1, and only interesting once there is more than one.
 *   c<atom>   a highlight on one atom's label.
 *   e<atom>   Lewis electrons on one atom, four digits clockwise from the top. The SAME encoding
 *             the text mark uses, because they are the same claim about an atom; see
 *             `reaction-lewis`, which owns it.
 *   l<atom>   a number or letter printed beside one atom. The teacher's own, NOT the index: a
 *             mechanism numbers carbons along the chain and SMILES numbers them in the order
 *             they were typed, and printing one where the other is meant is a wrong answer.
 *
 * ── AND WHY THE VALUES ARE ESCAPED ────────────────────────────────────────────────────────────
 * A name is prose a teacher types. `|` already ends a field, and `;` and `=` now end a pair, so
 * a molecule called `cis; trans` would silently cut the token in half. Three characters and the
 * escape itself are percent-encoded on the way out, which is cheap and total.
 */

import { lewisValue, parseLewisValue } from './reaction-lewis';

export type StructureAnnotations = {
  /** Species names inside one drawing, by fragment number from 1. */
  names: Record<number, string>;
  /** Highlight per atom, as a palette hex. */
  colours: Record<number, string>;
  /** Lewis electrons per atom, four digits clockwise from the top. */
  electrons: Record<number, string>;
  /** The teacher's own number or letter per atom. */
  labels: Record<number, string>;
};

export const NO_ANNOTATIONS: StructureAnnotations =
  { names: {}, colours: {}, electrons: {}, labels: {} };

/** Each key's prefix, in the order they are written out, so a round trip is byte-stable. */
const SECTIONS = [
  ['n', 'names'],
  ['c', 'colours'],
  ['e', 'electrons'],
  ['l', 'labels'],
] as const;

const ESCAPES: [RegExp, string][] = [
  [/%/g, '%25'],
  [/\|/g, '%7C'],
  [/;/g, '%3B'],
  [/=/g, '%3D'],
];

const escape = (v: string) => ESCAPES.reduce((s, [from, to]) => s.replace(from, to), v);
/* `%25` LAST on the way back, or `%253B` would come back as a semicolon rather than as `%3B`. */
const unescape = (v: string) => v
  .replace(/%7C/gi, '|').replace(/%3B/gi, ';').replace(/%3D/gi, '=').replace(/%25/gi, '%');

const empty = (): StructureAnnotations => ({ names: {}, colours: {}, electrons: {}, labels: {} });

export function readAnnotations(text: string): StructureAnnotations {
  const out = empty();
  if (!text.trim()) return out;
  for (const pair of text.split(';')) {
    const at = pair.indexOf('=');
    if (at < 1) continue;
    const key = pair.slice(0, at);
    const value = unescape(pair.slice(at + 1));
    const section = SECTIONS.find(([prefix]) => key.startsWith(prefix));
    if (!section) continue;
    const digits = key.slice(section[0].length);
    /* A key with no number on it is malformed rather than a hint, and `Number('')` is 0 — so
       the digits are checked for rather than coerced, or `n=x` would name the first species.
       Dropped either way, as `arrowsFromText` drops an arrow it cannot read. */
    if (!/^\d+$/.test(digits)) continue;
    const index = Number(digits);
    if (!value) continue;
    out[section[1]][index] = section[1] === 'electrons' ? lewisValue(parseLewisValue(value)) : value;
  }
  return out;
}

export function writeAnnotations(a: StructureAnnotations): string {
  const parts: string[] = [];
  for (const [prefix, field] of SECTIONS) {
    for (const index of Object.keys(a[field]).map(Number).sort((x, y) => x - y)) {
      const value = a[field][index];
      if (!value) continue;
      parts.push(`${prefix}${index}=${escape(value)}`);
    }
  }
  return parts.join(';');
}

export const hasAnnotations = (a: StructureAnnotations): boolean =>
  SECTIONS.some(([, field]) => Object.keys(a[field]).length > 0);

/**
 * Drop anything pointing at an atom or a species the drawing no longer has.
 *
 * The SMILES is editable and these are indices into it, exactly as an arrow's ends are — so
 * shortening a chain can strand a colour on an atom that is gone. `pruneArrows` makes the same
 * decision for the same reason: drawn somewhere arbitrary is worse than not drawn.
 */
export function pruneAnnotations(
  a: StructureAnnotations, atoms: number, parts: number,
): StructureAnnotations {
  const keep = (src: Record<number, string>, limit: number, from: number) =>
    Object.fromEntries(Object.entries(src).filter(([k]) => Number(k) >= from && Number(k) < limit + from));
  return {
    names: keep(a.names, parts, 1),
    colours: keep(a.colours, atoms, 0),
    electrons: keep(a.electrons, atoms, 0),
    labels: keep(a.labels, atoms, 0),
  };
}

/**
 * The same annotations with every index moved up, for a drawing being appended to another.
 *
 * Joining two molecules puts the second one's atoms after the first's, so its annotations have
 * to move with them. See `structure-join`, which is the only caller and explains why appending
 * is the only safe direction.
 */
export function shiftAnnotations(
  a: StructureAnnotations, byAtom: number, byPart: number,
): StructureAnnotations {
  const move = (src: Record<number, string>, by: number) =>
    Object.fromEntries(Object.entries(src).map(([k, v]) => [Number(k) + by, v]));
  return {
    names: move(a.names, byPart),
    colours: move(a.colours, byAtom),
    electrons: move(a.electrons, byAtom),
    labels: move(a.labels, byAtom),
  };
}
